import { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { useNavigate, useLocation } from "react-router-dom";
import { Tables } from "@/integrations/supabase/types";
import { Capacitor } from "@capacitor/core"; 

// Safe way to check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Update the profile type to include photo_url
type ProfileType = Tables<'profiles'>;

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  signOut: async () => {},
});

// Key for storing the last path in localStorage
const LAST_PATH_KEY = 'metefy_last_path';
// Key for storing OAuth source (hero or auth)
const OAUTH_SOURCE_KEY = 'metefy_oauth_source';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Only use navigate and location in browser context
  const navigate = isBrowser ? useNavigate() : null;
  const location = isBrowser ? useLocation() : null;
  
  // Store the current path in localStorage whenever it changes
  useEffect(() => {
    if (isBrowser && location && user) {
      // Don't save auth-related paths
      if (!['/auth', '/auth/callback'].includes(location.pathname)) {
        localStorage.setItem(LAST_PATH_KEY, location.pathname);
      }
    }
  }, [location, user]);

  useEffect(() => {
    if (!isBrowser) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        // Update state
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setIsLoading(false);

        if (event === 'SIGNED_IN' && currentSession?.user && navigate) {
          // Save push token after login (for mobile)
          if (Capacitor.isNativePlatform()) {
            try {
              const { PushNotifications } = await import('@capacitor/push-notifications');
              const registration = await PushNotifications.checkPermissions();
              if (registration.receive === 'granted') {
                // Get the stored token and save it
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                  // Check if there's a pending token stored
                  const pendingToken = localStorage.getItem('metefy_pending_push_token');
                  if (pendingToken) {
                    // Save the pending token
                    const { error: upsertError } = await supabase
                      .from('device_push_tokens')
                      .upsert(
                        {
                          user_id: user.id,
                          token: pendingToken,
                          platform: 'android',
                        },
                        { onConflict: 'user_id,token' }
                      );
                    if (!upsertError) {
                      localStorage.removeItem('metefy_pending_push_token');
                      console.log('[Auth] Saved pending push token after login');
                    }
                  } else {
                    // Re-register to get token if needed
                    await PushNotifications.register();
                    // The token will be saved in the registration listener
                  }
                }
              }
            } catch (err) {
              console.error('[Auth] Error saving push token after login:', err);
            }
          }

          // Only navigate if this is a new sign-in
          const isNewSignIn = !localStorage.getItem('metefy_session_established');
          
          if (isNewSignIn) {
            localStorage.setItem('metefy_session_established', 'true');
            
            // Check OAuth source to determine redirect destination
            const oauthSource = localStorage.getItem(OAUTH_SOURCE_KEY);
            localStorage.removeItem(OAUTH_SOURCE_KEY); // Clean up
            
            // Check for pending action (open submit drawer)
            const pendingAction = localStorage.getItem('metefy_pending_action');
            
            if (oauthSource === 'hero' || oauthSource === 'found-items-map') {
              // Coming from "Get Started" button or Found Items Map - go to found items map
              // The pending action will be handled by FoundItemsMap component
              navigate('/found-items-map', { replace: true });
            } else if (pendingAction === 'open_submit_drawer') {
              // If there's a pending action to open submit drawer, go to found-items-map
              navigate('/found-items-map', { replace: true });
            } else {
              // Default: go to profile
              navigate('/profile', { replace: true });
            }
          }

          // Generate username if profile doesn't exist or doesn't have username
          setTimeout(async () => {
            try {
              const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('username, photo_url')
                .eq('id', currentSession.user.id)
                .maybeSingle();

              // If profile doesn't exist, create it with username
              if (profileError && profileError.code === 'PGRST116') {
                const generateUniqueUsername = async (): Promise<string> => {
                  let attempts = 0;
                  while (attempts < 50) {
                    const randomNum = Math.floor(Math.random() * 1000000);
                    const username = `user_${randomNum.toString().padStart(6, '0')}`;
                    
                    const { data: existing } = await supabase
                      .from('profiles')
                      .select('id')
                      .eq('username', username)
                      .maybeSingle();
                    
                    if (!existing) {
                      return username;
                    }
                    attempts++;
                  }
                  // Fallback: use UUID part
                  return `user_${currentSession.user.id.substring(0, 8)}`;
                };

                const username = await generateUniqueUsername();
                
                await supabase
                  .from('profiles')
                  .insert({
                    id: currentSession.user.id,
                    username: username,
                    photo_url: currentSession.user.user_metadata?.avatar_url || null,
                  });
              } else if (profileData && !profileData.username) {
                // Profile exists but no username - generate one
                const generateUniqueUsername = async (): Promise<string> => {
                  let attempts = 0;
                  while (attempts < 50) {
                    const randomNum = Math.floor(Math.random() * 1000000);
                    const username = `user_${randomNum.toString().padStart(6, '0')}`;
                    
                    const { data: existing } = await supabase
                      .from('profiles')
                      .select('id')
                      .eq('username', username)
                      .maybeSingle();
                    
                    if (!existing) {
                      return username;
                    }
                    attempts++;
                  }
                  // Fallback: use UUID part
                  return `user_${currentSession.user.id.substring(0, 8)}`;
                };

                const username = await generateUniqueUsername();
                
                await supabase
                  .from('profiles')
                  .update({ username })
                  .eq('id', currentSession.user.id);
              }

              // Update photo_url if needed
              const avatarUrl = currentSession.user.user_metadata?.avatar_url;
              if (avatarUrl) {
                const { data: updatedProfile } = await supabase
                  .from('profiles')
                  .select('photo_url')
                  .eq('id', currentSession.user.id)
                  .single();

                if (updatedProfile && !updatedProfile.photo_url) {
                  await supabase
                    .from('profiles')
                    .update({ photo_url: avatarUrl })
                    .eq('id', currentSession.user.id);
                }
              }
            } catch (e) {
              // Handle error silently
            }
          }, 1000);
        } else if (event === 'SIGNED_OUT' && navigate) {
          // Clear session flag on sign out
          localStorage.removeItem('metefy_session_established');
          navigate('/', { replace: true });
        }
      }
    );

    // Initial session check
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!user && !session) { 
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
      }
      setIsLoading(false);
      
      if (initialSession?.user && navigate) {
        const currentPath = window.location.pathname;
        
        // Only redirect if coming from auth pages and session isn't established
        if (['/auth', '/auth/callback'].includes(currentPath) && !localStorage.getItem('metefy_session_established')) {
          localStorage.setItem('metefy_session_established', 'true');
          
          // Check OAuth source to determine redirect destination
          const oauthSource = localStorage.getItem(OAUTH_SOURCE_KEY);
          localStorage.removeItem(OAUTH_SOURCE_KEY); // Clean up
          
          // Check for pending action (open submit drawer)
          const pendingAction = localStorage.getItem('metefy_pending_action');
          
          if (oauthSource === 'hero' || oauthSource === 'found-items-map') {
            // Coming from "Get Started" button or Found Items Map - go to found items map
            // The pending action will be handled by FoundItemsMap component
            navigate('/found-items-map', { replace: true });
          } else if (pendingAction === 'open_submit_drawer') {
            // If there's a pending action to open submit drawer, go to found-items-map
            navigate('/found-items-map', { replace: true });
          } else {
            // Default: go to profile
            navigate('/profile', { replace: true });
          }
        }
        // Otherwise, stay on current page
      }
    }).catch(() => {
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const signOut = async () => {
    try {
      // Clear all local storage items
      localStorage.clear();
      
      // Clear session flag
      localStorage.removeItem('metefy_session_established');
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear user and session state
      setUser(null);
      setSession(null);
      
      // Navigate to home page
      if (navigate) {
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if there's an error, try to clear local state
      setUser(null);
      setSession(null);
      if (navigate) {
        navigate('/', { replace: true });
      }
    }
  };

  const value = {
    user,
    session,
    isLoading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const navigate = isBrowser ? useNavigate() : null;

  useEffect(() => {
    if (!isBrowser) return;
    
    if (!isLoading && !user && navigate) {
      navigate('/auth', { replace: true });
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return user ? <>{children}</> : null;
};
